import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { refreshShopLabels, setShop, setToken } from '../utils/helpers';
import {
  LookupAnswer,
  OrderToken,
  WithdrawalLookup,
  WithdrawalSession,
} from '../utils/types';
import { useTranslation } from 'react-i18next';
import { Toast, ToastContainer } from 'react-bootstrap';
import {
  errorBodyOf,
  isPortalInactive,
  isWithdrawalDisabled,
  isWithdrawalLookup,
  RefusalCode,
  refusalCodeOf,
  parseQrToken,
  postOrderByQr,
  postOrderByQrStandalone,
  postOrderByHandoff,
  postOrderLookup,
  QrFailure,
  qrFailureOf,
  situationOf,
  VerifiedShop,
  verifiedShopOf,
} from '../services/orderLookupService';
import { useStandaloneQr } from '../contexts/StandaloneQrContext';
import { parseHandoffToken, standaloneNavigationState } from '../utils/qrEntry';
import HandoffUnavailable from './HandoffUnavailable';
import { loadReturnFlow } from '../services/returnFlowLoader';
import { bindStandaloneOrder } from '../services/standaloneQrSession';
import {
  postWithdrawalSession,
  postWithdrawalSessionByQrStandalone,
  verifiedWithdrawalShopOf,
  withdrawalEntryVisible,
} from '../services/withdrawalService';
import { storePortalSession } from '../services/portalCredentials';
import { shopDomainOf } from '../services/portalSettings';
import { useStartTexts } from '../i18n/useStartTexts';
import { boundStartShop } from '../utils/helpers';
import { clearMethodsDrafts } from '../utils/methodsDraft';

interface FormData {
  orderNumber: string;
  clientInfo: string;
}

// #106 review r1: the field errors are translation KEYS, translated at
// render, so a language switch (in place, no remount) updates them; `logic`
// is the server message, shown as received; #119 / #122: `logicCode` (a
// refusal code) replaces it with its localized reason (translated at render).
interface FormErrors {
  orderNumber?: string | null;
  clientInfo?: string | null;
  logic?: string;
  logicCode?: RefusalCode;
  // #149 (D5) / #154: a withdrawal-routed lookup of a shop whose portal is
  // switched off (410 WITHDRAWAL_PORTAL_INACTIVE)
  portalInactive?: boolean;
  disabledByShop?: boolean;
}

interface Touched {
  orderNumber?: boolean;
  clientInfo?: boolean;
}

// The return search (manual or QR) keeps the routed response dispatcher.
// #164: /withdrawal/login reuses its manual identification fields and
// storefront credentials, but calls the dedicated session endpoint.
const OrderFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['translation', 'dynamic']);
  // #149: false once the page is gone (a StrictMode replay sets it again; the
  // `rq` strip below is a replace on the same page and never clears it); a
  // lookup answering after that installs nothing and navigates nowhere
  const aliveRef = useRef(true);
  // #496 (review r2 #1): the lookup form starts a new flow; a preferences
  // draft of the previous one (bank details included) is dropped
  useEffect(() => {
    clearMethodsDrafts();
  }, []);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const { search, pathname } = useLocation();
  const withdrawalLogin = pathname === '/withdrawal/login';
  const queryParams = new URLSearchParams(search);
  const shopParam = queryParams.get('shop');
  const proofParam = queryParams.get('proof');
  const withdrawalNeedsStorefront =
    withdrawalLogin && (!proofParam || !shopParam);

  // #70: `rq` is a parcel QR token forwarded by the storefront. It is read
  // once, never logged or stored, and removed from the visible URL.
  const [qrToken] = useState(() =>
    withdrawalLogin ? null : parseQrToken(queryParams.get('rq'))
  );
  const [handoffToken] = useState(() =>
    withdrawalLogin ? null : parseHandoffToken(queryParams.get('h'))
  );
  const [handoffUnavailable, setHandoffUnavailable] = useState(false);
  const [handoffRateLimited, setHandoffRateLimited] = useState(false);
  const [qrPending, setQrPending] = useState(
    () => qrToken !== null && !!proofParam && !!shopParam
  );
  const [qrFailure, setQrFailure] = useState<QrFailure | null>(null);
  const qrStarted = useRef(false);
  // #72: full-screen QR entry (no proof, no shop): the shop comes only from
  // the verified redemption, and there is no lookup form to fall back to.
  const standalone = useStandaloneQr();
  // #493 (S3): the 11 start texts of the URL shop (returns start page only;
  // /withdrawal/login and the standalone QR keep their static texts)
  const startDomain = shopDomainOf(search);
  const start = useStartTexts(
    !withdrawalLogin &&
      !standalone.active &&
      startDomain !== null &&
      startDomain === boundStartShop()
  );
  const [standaloneEntry] = useState(
    () =>
      standalone.active &&
      (standalone.handoff ? handoffToken !== null : qrToken !== null) &&
      !proofParam &&
      !shopParam
  );
  // #119 / #122: the refusal code of a refused redemption (null: no reason)
  const [standaloneReason, setStandaloneReason] = useState<RefusalCode | null>(
    null
  );
  // #149 (D5) / #154: a full-screen QR routed to a withdrawal of a shop whose
  // portal is off
  const [standalonePortalInactive, setStandalonePortalInactive] =
    useState(false);

  // #178: the withdrawal entry exists only in the full-screen QR mode, never
  // on the storefront return form (proof + shop)
  const standaloneWithdrawalEntry =
    !withdrawalLogin && standalone.active && !standalone.handoff;
  const [entryVisible, setEntryVisible] = useState(true);
  useEffect(() => {
    if (!standaloneWithdrawalEntry) return;
    const controller = new AbortController();
    setEntryVisible(true);
    void withdrawalEntryVisible(shopParam, controller.signal).then(visible => {
      if (!controller.signal.aborted) setEntryVisible(visible);
    });
    return () => controller.abort();
  }, [shopParam, standaloneWithdrawalEntry]);
  const [withdrawalStarting, setWithdrawalStarting] = useState(false);
  // #166 (codex r2): the full-screen QR's return redemption, from the request
  // to the end of the return loading (a refusal of the requirements after a
  // successful redemption ends it too, without a standalone failure)
  const [standaloneRedeeming, setStandaloneRedeeming] = useState(
    () => standaloneEntry
  );
  const openWithdrawalLogin = () => {
    const params = new URLSearchParams();
    if (proofParam) params.set('proof', proofParam);
    if (shopParam) params.set('shop', shopParam);
    navigate(
      { pathname: '/withdrawal/login', search: params.toString() },
      {
        state: standalone.active
          ? standaloneNavigationState(standalone.handoff, standalone.account)
          : undefined,
      }
    );
  };
  // #166: the full-screen QR withdraws with the QR kept in memory (no proof,
  // so no identification form could be submitted). A refusal or failure
  // falls back to the identification page with the guidance through the shop.
  const withdrawWithStandaloneQr = async (qr: string) => {
    setWithdrawalStarting(true);
    try {
      const response = await postWithdrawalSessionByQrStandalone(qr);
      if (!response.ok) {
        const body = await errorBodyOf(response);
        if (!aliveRef.current) return;
        if (isWithdrawalDisabled(response.status, body)) {
          setErrors({ disabledByShop: true });
        } else if (isPortalInactive(response.status, body)) {
          // the typed notice replaces any refusal of the return shown here
          setErrors({ portalInactive: true });
        } else {
          openWithdrawalLogin();
        }
        return;
      }
      const session = await response.json();
      if (!aliveRef.current) return;
      const verifiedShop = verifiedWithdrawalShopOf(session);
      if (verifiedShop === null) {
        openWithdrawalLogin();
        return;
      }
      standalone.succeed(verifiedShop, session.token);
      bindStandaloneOrder(session.order.id);
      await continueWithWithdrawal(
        { ...session, purpose: 'WITHDRAWAL', order_details: session.order },
        verifiedShop
      );
    } catch {
      if (aliveRef.current) openWithdrawalLogin();
    } finally {
      if (aliveRef.current) setWithdrawalStarting(false);
    }
  };
  const withdrawalEntry =
    standaloneWithdrawalEntry && entryVisible ? (
      <div className="xc-withdrawal-entry">
        <button
          type="button"
          className="xc-btn xc-btn--withdraw xc-btn--block"
          disabled={
            withdrawalStarting ||
            // #166: the full-screen QR withdraws only once its return
            // redemption has ended; until then it may still navigate or end
            // the session
            (standaloneEntry && standaloneRedeeming)
          }
          onClick={() => {
            if (standaloneEntry && qrToken) {
              void withdrawWithStandaloneQr(qrToken);
            } else {
              openWithdrawalLogin();
            }
          }}
        >
          {t('withdrawal.entryButton')}
        </button>
      </div>
    ) : null;

  const [formData, setFormData] = useState<FormData>({
    orderNumber: '',
    clientInfo: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Touched>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastTitle, setToastTitle] = useState('');

  const showAlert = (message: string, title: string = 'Notification') => {
    setToastMessage(message);
    setToastTitle(title);
    setShowToast(true);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.orderNumber.trim()) {
      newErrors.orderNumber = 'forms.submit.orderNumberRequired';
    }

    if (!formData.clientInfo.trim()) {
      newErrors.clientInfo = 'forms.submit.clientInfoRequired';
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientInfo) &&
      !/^\+?[\d\-\s().]{10,15}$/.test(formData.clientInfo)
    ) {
      newErrors.clientInfo = 'forms.submit.clientInfoInvalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
    setErrors(prev => ({
      ...prev,
      logic: undefined,
      logicCode: undefined,
      portalInactive: undefined,
      disabledByShop: withdrawalLogin ? prev.disabledByShop : undefined,
    }));
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, value);
  };

  const validateField = (name: string, value: string) => {
    setErrors(prev => ({
      ...prev,
      [name]: !value.trim()
        ? name === 'orderNumber'
          ? 'forms.numberCorrect'
          : 'forms.infoCorrect'
        : null,
    }));
  };

  // After a successful POST /orders or POST /orders/by-qr (same body): keep
  // the client token and shop, load the shop labels, then the requirements,
  // settings and methods (returnFlowLoader), and open the order details.
  // #72: with a verified shop (full-screen QR) token and shop are already
  // installed in this tab's session and are never written to the storage
  // shared with other tabs; the next steps get the verified domain.
  // #149: a page that is gone (navigation) stores nothing, navigates nowhere
  // and touches no state.
  const continueWithOrder = async (
    jsonDataOrder: OrderToken,
    verifiedShop: VerifiedShop | null = null
  ) => {
    if (!aliveRef.current) return;
    if (verifiedShop === null) {
      // #154 (Codex r2): one atomic session record (with the order) + the
      // legacy keys
      storePortalSession({
        token: jsonDataOrder.token,
        shopId: jsonDataOrder.order_details.shop_id,
        orderId: jsonDataOrder.order_details.id,
      });
      setToken(jsonDataOrder.token);
      setShop(jsonDataOrder.order_details.shop_id);
    }
    // #64: the shop is known only now; load its labels before navigating to
    // the next step. #95: in the requested language (a pending selection
    // included), never cancelling a newer selection.
    await refreshShopLabels();
    if (!aliveRef.current) return;
    const result = await loadReturnFlow(jsonDataOrder, {
      proof: verifiedShop ? null : proofParam,
      shop: verifiedShop ? verifiedShop.domain : shopParam,
      standalone: verifiedShop !== null,
    });
    if (!aliveRef.current) return;
    if (result.kind === 'portalInactive' || result.kind === 'disabledByShop') {
      // #154: typed through the shared loader (never with a RETURN session
      // in practice; the notice of the lookup)
      setErrors(prevState => ({
        ...prevState,
        logic: undefined,
        logicCode: undefined,
        [result.kind]: true,
      }));
    } else if (result.kind === 'refused') {
      // The order lookup already succeeded (2xx) here, so the 5xx branch the
      // lookup used to test on its own response never applied. #122: a 403
      // window / rule denial carries its code, shown as a localized reason.
      const code = result.code;
      // #154: a refusal with nothing to show (e.g. an empty 403 body) is the
      // generic error of the caller, as before
      if (code === null && !result.message) {
        throw new Error(`return flow refused: ${result.status}`);
      }
      setErrors(prevState => ({
        ...prevState,
        logic: code === null ? result.message : undefined,
        logicCode: code ?? undefined,
      }));
      // Reset form
      setFormData({
        orderNumber: '',
        clientInfo: '',
      });
    } else {
      navigate('/order-details', {
        state: {
          ...result.state,
          ...(verifiedShop
            ? standaloneNavigationState(standalone.handoff, standalone.account)
            : {}),
        },
      });
    }
  };

  // #149 / #154: after a WITHDRAWAL-routed lookup / QR redemption: keep the
  // WITHDRAWAL token exactly as a return token (same slot, refreshed the same
  // way; a verified full-screen session stays in memory), load the shop
  // labels and open the declaration form with the situation preview. No
  // return loader: the withdrawal has no return gates.
  const continueWithWithdrawal = async (
    answer: WithdrawalLookup,
    verifiedShop: VerifiedShop | null = null
  ) => {
    if (!aliveRef.current) return;
    if (verifiedShop === null) {
      storePortalSession({
        token: answer.token,
        shopId: answer.order_details.shop_id,
        orderId: answer.order_details.id,
      });
      setToken(answer.token);
      setShop(answer.order_details.shop_id);
    }
    await refreshShopLabels();
    if (!aliveRef.current) return;
    // #166 (codex r1): the token is NOT copied into the navigation state, which
    // the browser keeps in its history (it survives a reload); the form reads
    // it from the session credentials (stored, or in memory for the QR).
    const session: WithdrawalSession = {
      token: '',
      order: answer.order_details,
      prefill_name: answer.prefill_name ?? null,
      prefill_email: answer.prefill_email ?? null,
    };
    navigate('/withdrawal/form', {
      state: {
        session,
        situation: situationOf(answer.situation),
        proof: verifiedShop ? null : proofParam,
        shop: verifiedShop ? verifiedShop.domain : shopParam,
        ...(verifiedShop
          ? standaloneNavigationState(standalone.handoff, standalone.account)
          : {}),
      },
    });
  };

  // #154: the ONE response dispatcher of the manual lookup and both QR
  // variants. The answer is narrowed here, before the (unchanged) return
  // loader ever sees it.
  const dispatchLookup = async (
    answer: LookupAnswer,
    verifiedShop: VerifiedShop | null = null
  ) => {
    if (isWithdrawalLookup(answer)) {
      await continueWithWithdrawal(answer, verifiedShop);
    } else {
      await continueWithOrder(answer, verifiedShop);
    }
  };

  const redeemQrToken = async (qr: string, proof: string, shop: string) => {
    try {
      const response = await postOrderByQr(qr, proof, shop);
      if (!response.ok) {
        // #154: 410 WITHDRAWAL_PORTAL_INACTIVE -> 'portalInactive', before
        // the refusal mapping
        const failure = await qrFailureOf(response);
        if (aliveRef.current) setQrFailure(failure);
        return;
      }
      const answer: LookupAnswer = await response.json();
      await dispatchLookup(answer);
    } catch {
      if (aliveRef.current) setQrFailure('unavailable');
    } finally {
      if (aliveRef.current) setQrPending(false);
    }
  };

  const redeemStandaloneQr = async (qr: string) => {
    try {
      const response = await (
        standalone.handoff ? postOrderByHandoff : postOrderByQrStandalone
      )(qr);
      if (!response.ok) {
        const body = await errorBodyOf(response);
        if (!aliveRef.current) return;
        setHandoffRateLimited(
          standalone.handoff === true && response.status === 429
        );
        if (
          standalone.handoff &&
          response.status === 404 &&
          (body as { code?: unknown } | null)?.code === 'handoff_unavailable'
        ) {
          setHandoffUnavailable(true);
        } else if (isWithdrawalDisabled(response.status, body)) {
          setErrors({ disabledByShop: true });
        } else if (isPortalInactive(response.status, body)) {
          setStandalonePortalInactive(true);
        } else {
          setStandaloneReason(refusalCodeOf(response.status, body));
        }
        standalone.fail();
        return;
      }
      const body = await response.json();
      if (!aliveRef.current) return;
      // the shop is accepted only when it is the shop of the order, for a
      // return and a withdrawal answer alike
      const verifiedShop = verifiedShopOf(body);
      if (verifiedShop === null) {
        standalone.fail();
        return;
      }
      standalone.succeed(verifiedShop, body.token);
      // #154: the in-memory session is bound to its order
      bindStandaloneOrder((body as LookupAnswer).order_details.id);
      await dispatchLookup(body as LookupAnswer, verifiedShop);
    } catch {
      if (aliveRef.current) standalone.fail();
    } finally {
      if (aliveRef.current) setStandaloneRedeeming(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (!params.has('rq') && !params.has('h')) return;
    params.delete('rq');
    params.delete('h');
    const rest = params.toString();
    navigate(
      { search: rest ? `?${rest}` : '' },
      {
        replace: true,
        state: standalone.active
          ? standaloneNavigationState(standalone.handoff, standalone.account)
          : undefined,
      }
    );
  }, [
    search,
    navigate,
    standalone.active,
    standalone.handoff,
    standalone.account,
  ]);

  useEffect(() => {
    const token = standalone.handoff ? handoffToken : qrToken;
    if (qrStarted.current || !token) return;
    if (standaloneEntry) {
      qrStarted.current = true;
      redeemStandaloneQr(token);
      return;
    }
    if (!qrPending) return;
    qrStarted.current = true;
    redeemQrToken(token, proofParam as string, shopParam as string);
    // Runs once per page load (qrStarted): the token and proof are read on
    // the first render and the URL is rewritten right after.
  }, [qrPending, qrToken, handoffToken, standalone.handoff]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (withdrawalLogin && (errors.disabledByShop || !proofParam || !shopParam))
      return;

    if (!validateForm()) {
      return;
    }

    setTouched({
      orderNumber: true,
      clientInfo: true,
    });

    setIsSubmitting(true);

    // get order details
    // get order & shop requirements for return
    try {
      const params = {
        order_number: formData.orderNumber,
        customer_email_or_phone_number: formData.clientInfo,
      };
      const response = await (
        withdrawalLogin ? postWithdrawalSession : postOrderLookup
      )(params, proofParam, shopParam);
      if (!response.ok) {
        const error = await response.json();
        console.error('Error:', error);
        if (!aliveRef.current) return;
        // Reset form
        setFormData({
          orderNumber: '',
          clientInfo: '',
        });
        if (isWithdrawalDisabled(response.status, error)) {
          setErrors({ disabledByShop: true });
        } else if (isPortalInactive(response.status, error)) {
          // #149 (D5) / #154: a withdrawal-routed lookup of a shop whose
          // portal was switched off; before the refusal mapping
          setErrors(prevState => ({
            ...prevState,
            logic: undefined,
            logicCode: undefined,
            portalInactive: true,
          }));
        } else if (response.status >= 500) {
          showAlert(`${error?.message}`, 'Error');
        } else {
          const code = refusalCodeOf(response.status, error);
          setErrors(prevState => ({
            ...prevState,
            logic: code === null ? error?.message : undefined,
            logicCode: code ?? undefined,
          }));
        }
      } else {
        if (withdrawalLogin) {
          const session: WithdrawalSession = await response.json();
          if (!aliveRef.current) return;
          const verifiedShop = standalone.active
            ? {
                id: session.order.shop_id,
                domain: shopParam as string,
                name: null,
              }
            : null;
          if (verifiedShop) {
            standalone.succeed(verifiedShop, session.token);
            bindStandaloneOrder(session.order.id);
          }
          await continueWithWithdrawal(
            { ...session, purpose: 'WITHDRAWAL', order_details: session.order },
            verifiedShop
          );
        } else {
          const answer: LookupAnswer = await response.json();
          await dispatchLookup(answer);
        }
      }
    } catch (error) {
      console.error(error);
      if (aliveRef.current) {
        showAlert(`An error occurred while initializing return.`, 'Error');
      }
    } finally {
      if (aliveRef.current) setIsSubmitting(false);
    }
  };

  const title = (
    <div className="xc-title-row">
      <h2 className="xc-title">
        {withdrawalLogin
          ? t('withdrawal.loginTitle')
          : (start.text('start_title', 'forms.title') ?? t('forms.title'))}
      </h2>
    </div>
  );

  const loadingStatus = (
    <div className="xc-status" role="status">
      <span className="xc-spinner" aria-hidden="true"></span>
      {t('forms.qrLoading')}
    </div>
  );

  // #119 / #122: the localized reason of a refused lookup, per code (each key
  // a literal, see the orphan-key gate)
  const lookupReason = (code: RefusalCode): string => {
    switch (code) {
      case 'ORDER_NOT_FULFILLED':
        return t('forms.lookupNotFulfilled');
      case 'SECOND_RETURN_NOT_ALLOWED':
        return t('forms.secondReturnNotAllowed');
      case 'ALREADY_RETURNED':
        return t('forms.lookupAlreadyReturned');
      case 'RETURN_WINDOW_PASSED':
        return t('forms.lookupWindowPassed');
      case 'RETURN_DENIED_BY_RULE':
        return t('forms.lookupDeniedByRule');
    }
  };

  const standaloneMessage = (code: RefusalCode | null): string => {
    switch (code) {
      case 'ORDER_NOT_FULFILLED':
        return t('forms.qrStandaloneNotFulfilled');
      case 'SECOND_RETURN_NOT_ALLOWED':
        return t('forms.secondReturnNotAllowed');
      case 'ALREADY_RETURNED':
        return t('forms.qrStandaloneAlreadyReturned');
      case 'RETURN_WINDOW_PASSED':
        return t('forms.qrStandaloneWindowPassed');
      case 'RETURN_DENIED_BY_RULE':
        return t('forms.qrStandaloneDeniedByRule');
      default:
        return t('forms.qrStandaloneUnavailable');
    }
  };

  const qrFailureMessage = (failure: QrFailure): string => {
    switch (failure) {
      case 'notFulfilled':
        return t('forms.qrNotFulfilled');
      case 'secondReturnNotAllowed':
        return t('forms.secondReturnNotAllowed');
      case 'alreadyReturned':
        return t('forms.qrAlreadyReturned');
      case 'windowPassed':
        return t('forms.qrWindowPassed');
      case 'deniedByRule':
        return t('forms.qrDeniedByRule');
      case 'notReturnable':
        return t('forms.qrNotReturnable');
      case 'disabledByShop':
        return t('withdrawal.disabledByShop');
      case 'portalInactive':
        return t('withdrawal.portalInactive');
      default:
        return t('forms.qrUnavailable');
    }
  };

  const logicError = errors.disabledByShop ? (
    <div className="xc-notice xc-notice--withdraw" role="alert">
      {t('withdrawal.disabledByShop')}
    </div>
  ) : errors.portalInactive ? (
    <div className="xc-notice xc-notice--warn" role="alert">
      {t('withdrawal.portalInactive')}
    </div>
  ) : errors.logicCode || errors.logic ? (
    <div className="xc-notice xc-notice--error" role="alert">
      {errors.logicCode ? lookupReason(errors.logicCode) : errors.logic}
    </div>
  ) : null;

  if (standalone.active && !withdrawalLogin) {
    // Standalone failures offer account recovery for consumed handoff tickets.
    return (
      <div className="xc-screen">
        {title}
        {standalone.handoff && (handoffUnavailable || !standaloneEntry) ? (
          <HandoffUnavailable account={standalone.account ?? null} />
        ) : (
          (logicError ??
          (standaloneEntry && standalone.status !== 'failure' ? (
            loadingStatus
          ) : (
            <div className="xc-notice xc-notice--warn" role="status">
              {standalonePortalInactive
                ? t('withdrawal.portalInactive')
                : standaloneMessage(standaloneReason)}
            </div>
          )))
        )}
        {handoffRateLimited && standalone.account && (
          <HandoffUnavailable account={standalone.account} />
        )}
        {withdrawalEntry}
      </div>
    );
  }

  // #493 (S3): the render gate - the shop texts (cache or answer), else up to
  // 1.5 s, then the static texts; no static text flashes first
  if (!start.ready) {
    return (
      <div className="xc-screen" aria-busy="true">
        <div className="xc-status" role="status">
          <span className="xc-spinner" aria-hidden="true"></span>
        </div>
      </div>
    );
  }

  // an error shows only once its field was touched (blur or a valid submit)
  // (the stored key is translated here, in the current language)
  const fieldError = (name: keyof Touched) => {
    const key = errors[name];
    return touched[name] && key ? String(t(key)) : null;
  };
  const orderNumberError = fieldError('orderNumber');
  const clientInfoError = fieldError('clientInfo');

  return (
    <div className="xc-screen">
      <ToastContainer position="top-center" className="xc-toast">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="xc-toast__title">{toastTitle}</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      {title}
      {withdrawalNeedsStorefront && (
        <div className="xc-notice xc-notice--withdraw" role="status">
          {t('withdrawal.reopenFromShop')}
        </div>
      )}
      {qrPending ? (
        loadingStatus
      ) : (
        <>
          {qrFailure && (
            <div
              className={
                qrFailure === 'disabledByShop'
                  ? 'xc-notice xc-notice--withdraw'
                  : 'xc-notice xc-notice--warn'
              }
              role="status"
            >
              {qrFailureMessage(qrFailure)}
            </div>
          )}
          <form className="xc-card" onSubmit={handleSubmit}>
            <div className="xc-field">
              <label htmlFor="orderNumber" className="xc-field__label">
                {start.text('start_order_number_label', 'forms.orderNumber') ??
                  t('forms.orderNumber')}{' '}
                <span className="xc-field__req">*</span>
              </label>
              <input
                type="text"
                className="xc-input"
                id="orderNumber"
                name="orderNumber"
                value={formData.orderNumber}
                onBlur={handleBlur}
                onChange={handleInputChange}
                placeholder={
                  start.text(
                    'start_order_number_placeholder',
                    'forms.orderNumberPlaceholder'
                  ) ??
                  t('forms.orderNumberPlaceholder') ??
                  'Enter order number'
                }
                disabled={isSubmitting || withdrawalNeedsStorefront}
                aria-invalid={errors.orderNumber ? true : undefined}
                aria-describedby={
                  orderNumberError ? 'orderNumber-error' : undefined
                }
              />
              {orderNumberError && (
                <p
                  className="xc-field__error"
                  id="orderNumber-error"
                  role="alert"
                >
                  {orderNumberError}
                </p>
              )}
            </div>

            <div className="xc-field">
              <label htmlFor="clientInfo" className="xc-field__label">
                {start.text('start_contact_label', 'forms.clientInfo') ??
                  t('forms.clientInfo')}{' '}
                <span className="xc-field__req">*</span>
              </label>
              <input
                type="text"
                className="xc-input"
                id="clientInfo"
                name="clientInfo"
                value={formData.clientInfo}
                onBlur={handleBlur}
                onChange={handleInputChange}
                placeholder={
                  start.text(
                    'start_contact_placeholder',
                    'forms.clientInfoPlaceholder'
                  ) ??
                  t('forms.clientInfoPlaceholder') ??
                  'Enter client email/phone'
                }
                disabled={isSubmitting || withdrawalNeedsStorefront}
                aria-invalid={errors.clientInfo ? true : undefined}
                aria-describedby={
                  clientInfoError
                    ? 'clientInfo-help clientInfo-error'
                    : 'clientInfo-help'
                }
              />
              <p className="xc-field__help" id="clientInfo-help">
                {withdrawalLogin
                  ? t('withdrawal.loginHelp')
                  : (start.text('start_help', 'forms.details') ??
                    t('forms.details'))}
              </p>
              {clientInfoError && (
                <p
                  className="xc-field__error"
                  id="clientInfo-error"
                  role="alert"
                >
                  {clientInfoError}
                </p>
              )}
            </div>

            <button
              type="submit"
              className={
                withdrawalLogin
                  ? 'xc-btn xc-btn--withdraw xc-btn--block'
                  : 'xc-btn xc-btn--primary xc-btn--block'
              }
              disabled={
                isSubmitting ||
                (withdrawalLogin && errors.disabledByShop) ||
                withdrawalNeedsStorefront
              }
            >
              {isSubmitting ? (
                <>
                  <span className="xc-spinner" aria-hidden="true"></span>
                  {t('buttons.submitting')}
                </>
              ) : withdrawalLogin ? (
                t('withdrawal.entryButton')
              ) : (
                (start.text('start_button', 'buttons.startReturn') ??
                t('buttons.startReturn'))
              )}
            </button>
            {logicError}
          </form>
          {withdrawalEntry}
          {!withdrawalLogin && (
            <section className="xc-card xc-card--quiet">
              <h3 className="xc-card__title">
                {start.text('start_how_title', 'start.how.title') ??
                  t('start.how.title')}
              </h3>
              <dl className="xc-kv">
                <dt>1</dt>
                <dd>
                  {start.text('start_how_1', 'start.how.1') ?? t('start.how.1')}
                </dd>
                <dt>2</dt>
                <dd>
                  {start.text('start_how_2', 'start.how.2') ?? t('start.how.2')}
                </dd>
                <dt>3</dt>
                <dd>
                  {start.text('start_how_3', 'start.how.3') ?? t('start.how.3')}
                </dd>
              </dl>
            </section>
          )}
        </>
      )}
      {qrPending && withdrawalEntry}
    </div>
  );
};

export default OrderFormPage;
